import { BOAT_SCHEMATICS } from "./schematic-library-boat";
import { VAN_SCHEMATICS } from "./schematic-library-van";
import { CABIN_SCHEMATICS } from "./schematic-library-cabin";
import { SCHEMATIC_CATEGORIES, SCHEMATIC_CATEGORY_ROUTES, SCHEMATIC_EXAMPLE_ROUTES, schematicExamplePath, type SchematicExampleId, type SchematicCategoryPageId } from "./schematic-library-routes";
import type { SchematicCategory, SchematicExample } from "./schematic-library-types";
import type { ResourcePageData, ResourcePageSource } from "./resource-page-data";

export const SCHEMATIC_EXAMPLES: SchematicExample[] = [...BOAT_SCHEMATICS, ...VAN_SCHEMATICS, ...CABIN_SCHEMATICS];

export function schematicExample(id: string): SchematicExample {
  const example = SCHEMATIC_EXAMPLES.find(example => example.id === id);
  if (!example) throw new Error(`Unknown schematic: ${id}`);
  return example;
}

export function schematicsInCategory(category: SchematicCategory): SchematicExample[] {
  return SCHEMATIC_EXAMPLES.filter(example => example.category === category);
}

function sources(example: SchematicExample): ResourcePageSource[] {
  return example.sources.map(source => ({ title: source.label, publisher: new URL(source.url).hostname.replace(/^www\./, ""), url: source.url, note: source.note }));
}

export const SCHEMATIC_RESOURCE_PAGES = Object.fromEntries([
  ...SCHEMATIC_EXAMPLE_ROUTES.map(route => {
    const example = schematicExample(route.id);
    const category = SCHEMATIC_CATEGORIES.find(category => category.id === example.category)!;
    const data: ResourcePageData = {
      ...route, canonicalUrl: `https://crimp.studio${route.path}`, title: example.seoTitle,
      description: `${example.summary} ${example.basis} Explore the editable schematic and cable sizes in Crimp.`,
      summary: example.summary, eyebrow: `${category.title} wiring example`,
      publishedAt: "2026-10-21", reviewedAt: "2026-10-21",
      breadcrumbs: [{ label: "Crimp", href: "/" }, { label: "Examples", href: "/examples/" }, { label: category.title, href: `/examples/${category.id}/` }, { label: example.title, href: route.path }],
      sources: sources(example),
      relatedLinks: schematicsInCategory(example.category).filter(other => other.id !== example.id).map(other => ({ title: other.title, href: schematicExamplePath(other.id), description: other.summary })),
    };
    return [route.id, data];
  }),
  ...SCHEMATIC_CATEGORY_ROUTES.map(route => {
    const category = SCHEMATIC_CATEGORIES.find(category => category.id === route.category)!;
    const examples = schematicsInCategory(category.id);
    const data: ResourcePageData = {
      ...route, canonicalUrl: `https://crimp.studio${route.path}`, title: category.heading,
      description: `${category.summary} ${examples.length} editable Crimp schematics based on manufacturer wiring diagrams, with cable sizes and practical notes.`,
      summary: category.summary, eyebrow: "Example schematics", publishedAt: "2026-10-21", reviewedAt: "2026-10-21",
      breadcrumbs: [{ label: "Crimp", href: "/" }, { label: "Examples", href: "/examples/" }, { label: category.title, href: route.path }],
      sources: Array.from(new Map(examples.flatMap(sources).map(source => [source.url, source])).values()),
      relatedLinks: examples.map(example => ({ title: example.title, href: schematicExamplePath(example.id), description: example.summary })),
    };
    return [route.id, data];
  }),
]) as Record<SchematicExampleId | SchematicCategoryPageId, ResourcePageData>;
